// 移植自 crm/src/components/Dialog.jsx。共用彈窗外殼（brand 皮膚），DOM 與 class 跟舊版一致（ROADMAP #12：不改用原生 <dialog>）。
// props：open、onClose、title、footer、children、className（卡片寬度，預設 max-w-[480px]）、bodyClassName（覆寫 body padding 等）、
//   busy（送出中鎖定：X、遮罩、Esc 都不關）、titleId／descId、
//   overflowVisible（含下拉的彈窗：卡片不裁切、body 用一般 div）、scrollStable（body 用原生捲軸）、
//   simpleLayout（無標題分隔線、24px 外圍留白的簡約彈窗，Figma 8900:48665）、compact（搭配 simpleLayout，Figma 9821:83653 的 148 型間距）
// 鍵盤：Esc 只關最上層（escapeLayers）；開啟時焦點移進卡片、頁面本體與下層彈窗 inert（背景 Tab 不到），關閉後焦點還給觸發元素。
//   舊版的 trapFocus prop 不再需要（inert 已涵蓋，而且不會擋到 Portal 到 body 的下拉），傳了也沒有作用。
import { useEffect, useId, useRef } from 'react'
import { createPortal } from 'react-dom'
import { CloseIcon } from './icons'
import { OverlayScroll } from './OverlayScroll'
import { useEscapeLayer } from '@/hooks/useEscapeLayer'
import { useInertBackground } from '@/hooks/useInertBackground'

export function Dialog({ open, onClose, title, footer, children, className, bodyClassName = '', busy, titleId, descId, overflowVisible, scrollStable, simpleLayout, compact }) {
  const internalTitleId = useId()
  const labelId = title ? (titleId ?? internalTitleId) : undefined
  const close = () => {
    if (!busy) onClose?.()
  }

  const cardRef = useRef(null)
  useEscapeLayer(open, close, cardRef)
  useInertBackground(open, cardRef)

  // 開啟時焦點移進卡片：背景 inert 後原本的焦點會被移除，不接住的話要多按幾次 Tab 才進得來。
  // 子元件已在掛載當下聚焦（autoFocus／子層 effect，effect 先子後父）就不搶
  useEffect(() => {
    if (open && !cardRef.current?.contains(document.activeElement)) cardRef.current?.focus({ preventScroll: true })
  }, [open])

  if (!open) return null

  // body：預設浮動捲軸；overflowVisible 不裁切；scrollStable 原生捲軸。
  // padding 用 max-md:p-3 而非 md:p-6：呼叫端 bodyClassName 的自訂 padding 才不會被 md: 前綴蓋掉
  let body
  if (overflowVisible) {
    body = <div className={`p-6 max-md:p-3 ${bodyClassName}`}>{children}</div>
  } else if (scrollStable) {
    body = <div className={`min-h-0 flex-1 overflow-y-auto scrollbar-stable p-6 max-md:p-3 ${bodyClassName}`}>{children}</div>
  } else {
    body = (
      <OverlayScroll rootClassName="flex-1" className={`${simpleLayout ? 'px-6' : 'p-6 max-md:p-3'} ${bodyClassName}`}>
        {children}
      </OverlayScroll>
    )
  }

  // Portal 到 body：避開祖先 transform（頁面過場動畫）讓 fixed 被限縮在內容區
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* 遮罩點擊關閉是滑鼠的補充操作，鍵盤用 Esc */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className="absolute inset-0 bg-black/40" onClick={close} />
      <div
        ref={cardRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelId}
        aria-describedby={descId}
        // outline-none：卡片只是接住焦點的容器，不顯示舊版沒有的焦點外框
        className={`relative flex w-full flex-col rounded-brand-base bg-base-100 outline-none ${simpleLayout ? 'shadow-[0px_3px_4.5px_0px_#2022210D]' : 'shadow-[0px_5px_9px_0px_#18191b0d]'} ${overflowVisible ? '' : 'max-h-[calc(100dvh-2rem)] overflow-hidden'} ${className ?? 'max-w-[480px]'}`}
      >
        <div className={simpleLayout ? `relative flex shrink-0 items-center pl-6 pr-8 ${compact ? 'pt-5 pb-3' : 'pt-6 pb-4'}` : 'relative flex shrink-0 items-center border-b border-brand-black-50 px-4 py-3.5 md:px-6'}>
          <h3 id={labelId} className={simpleLayout ? 'text-lg font-bold leading-6 tracking-[0.36px] text-brand-black-1000' : 'pr-9 text-base font-semibold leading-6 tracking-[0.16px] text-brand-black-1000'}>
            {title}
          </h3>
          <button type="button" className="absolute right-0 top-0 flex size-[42px] items-center justify-center text-brand-black-700 hover:text-brand-black-1000" onClick={close} aria-label="close">
            <CloseIcon className="size-3.5" />
          </button>
        </div>
        {body}
        {footer && <div className={simpleLayout ? `shrink-0 px-6 ${compact ? 'pb-5 pt-3' : 'pb-6 pt-7'}` : 'shrink-0 px-3 pb-3 md:px-6 md:pb-6'}>{footer}</div>}
      </div>
    </div>,
    document.body,
  )
}

export default Dialog
